import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useHistory } from 'react-router-dom';
import get from 'lodash/get';
import isEqual from 'lodash/isEqual';
import { batch } from 'react-redux';
import { store } from 'react-notifications-component';
import {
  loadRestaurantDetails,
  setCheckoutData,
  setPreset,
  resetCheckoutData,
} from 'redux/restaurants/actions';
import { updateUserCart, removeUserCart } from 'redux/userCart/actions';
import { toggleRestaurantFavorite } from 'redux/restaurants/actions';
import { showSignInModal } from 'redux/actions/modalActions';
import { validate } from 'api/authApi';
import { signoutRequest } from 'redux/actions/authActions';
import { priceDecimalFormatter } from 'formatter/preset.formatter';
import {
  checkMinumumHourNoticeValidation,
  getDeliveryInfo,
} from 'utils/address.util';
import {
  isRestaurantOpenNow,
  getOrderNoticeLabel,
} from 'utils/restaurants.util';
import { useDeliveryFee } from 'hooks/useDeliveryFee';
import {
  buildCheckoutDataFromPresetItem,
  getPresetSubTotalPrice,
  getPresetItemTotalPrice,
  getPresetItemQuantity,
  extractRecipientName,
  buildServingDetailsFromRecipientName,
} from 'utils/preset.util';
import { RestaurantView } from 'panda-ui/components/RestaurantView';
import { CartSidebar } from 'panda-ui/components/CartSidebar';
import { ItemModal } from 'panda-ui/components/ItemModal';
import { getGroupOrderURL } from 'api/apiUtils';
import {
  createGroupOrder,
  saveForLater,
  fetchFavoritesAndSavedOrders,
} from 'api/presetApi';
import DeliveryInfoFormInline from 'components/common/DeliveryInfoForm/DeliveryInfoFormInline';
import LoadingAnimation from 'components/common/LoadingAnimation';
import './index.scss';

const mapMenu = menu => ({
  id: menu.id,
  name: menu.name,
  category:
    menu.categoryName ||
    menu.presetMenuCategoryName ||
    menu.category ||
    'Additional Items',
  description: menu.description || '',
  fullDescription: menu.description || '',
  price: (menu.priceCents || 0) / 100,
  image:
    menu.imageUrl ||
    menu.menuImageUrl ||
    menu.mainImageUrl ||
    '/meal_default.png',
  isVegetarian: !!menu.isVegetarian,
  isPopular: !!menu.isPopular,
  calories: menu.calories || 0,
  minOrderAmount: menu.minOrderAmount || 1,
  servingStyle: menu.servingStyle || menu.per || '',
  options:
    menu.menuItems && menu.menuItems.length > 0
      ? menu.menuItems.map(mi => ({
          id: mi.id,
          name: mi.name,
          price: (mi.priceCents || 0) / 100,
        }))
      : [
          {
            id: menu.id,
            name: menu.name,
            price: (menu.priceCents || 0) / 100,
          },
        ],
});

const PandaRestaurantDetails = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { slug } = useParams();
  const {
    loadingPresetDetails,
    checkoutData,
    preset,
  } = useSelector(state => state.restaurants);
  const user = useSelector(state => state.user);
  const userSignedIn = get(user, 'user.confirmed', false);
  const receivingMethod = useSelector(state => state.receivingMethod);
  const [isFavorite, setIsFavorite] = useState(preset.favorite);
  const [activeItem, setActiveItem] = useState(null);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [isCreatingGroupOrder, setIsCreatingGroupOrder] = useState(false);
  const [isSavingForLater, setIsSavingForLater] = useState(false);
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveNameInput, setSaveNameInput] = useState('');

  useEffect(() => {
    dispatch(loadRestaurantDetails(slug));
  }, [slug, user?.jwt]);

  useEffect(() => {
    setIsFavorite(preset.favorite);
  }, [preset.favorite]);

  const {
    id,
    name,
    description,
    orderCount,
    chefs,
    presetItems,
    deliveryPriceCents,
    averageRating,
    preOrderNoticeHour,
    minimumOrderAmountCents,
    favorite,
    presetTags,
    storeHours,
    bannerImageUrl,
    availableForOrdering,
  } = preset || {};

  const acceptingOrders = availableForOrdering !== false;

  // preset.minimumOrderAmountCents/deliveryPriceCents are denormalized
  // snapshots that can fall out of sync with the chef's own settings -
  // fall back to the chef record so a stale/null preset value doesn't
  // silently show $0 or bypass the minimum-order check below.
  const chefRecord = chefs?.[0];
  const effectiveMinimumOrderAmountCents =
    minimumOrderAmountCents || chefRecord?.preOrderMinOrderAmountCents || 0;
  const effectiveDeliveryPriceCents =
    deliveryPriceCents || chefRecord?.deliveryFeeCents || 0;

  // Bumped whenever the delivery info bar at the top of this page saves a
  // change, so the address re-read below picks up the new value.
  const [deliveryBarVersion, setDeliveryBarVersion] = useState(0);
  const deliveryAddress = useMemo(
    () => getDeliveryInfo()?.address || '',
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [deliveryBarVersion],
  );
  // The real delivery fee is the live distance-based lookup - it only
  // falls back to the chef's flat delivery fee when that lookup can't be
  // completed, so this page and checkout always agree and neither one
  // blocks on a broken address/lookup.
  const { deliveryFee: liveDeliveryPriceCents } = useDeliveryFee(
    chefRecord?.id,
    deliveryAddress,
    effectiveDeliveryPriceCents,
  );

  const menuItems = useMemo(
    () => preset?.chefs?.flatMap(chef => chef.menus) || [],
    [preset],
  );
  const menuById = useMemo(
    () =>
      menuItems.reduce(
        (acc, menu) => ({ ...acc, [menu.id]: menu }),
        {},
      ),
    [menuItems],
  );

  const mappedMenuItems = useMemo(
    () => menuItems.map(mapMenu),
    [menuItems],
  );

  const mappedRestaurant = useMemo(() => {
    const chef = chefs?.[0];
    const address = [chef?.address1, chef?.city, chef?.state]
      .filter(Boolean)
      .join(', ');
    return {
      id,
      slug,
      name,
      tagline: description || '',
      coverImage: bannerImageUrl || '/panda_elegant_banner.jpg',
      avatarImage: chef?.profileImageUrl || '/panda_avatar.jpg',
      rating: averageRating || 0,
      reviewCount: 0,
      orderedCount: orderCount || 0,
      cuisine: chef?.cuisineType || presetTags?.[0]?.name || '',
      category: chef?.cuisineType || 'all',
      distanceMiles: 0.5,
      deliveryFee: liveDeliveryPriceCents / 100,
      deliveryTime: getOrderNoticeLabel(preOrderNoticeHour || 0),
      noticeHours: preOrderNoticeHour || 0,
      openNow: isRestaurantOpenNow(storeHours),
      minimumOrder: effectiveMinimumOrderAmountCents / 100,
      address: address || '',
      isNew: false,
      categories: [
        ...new Set([
          ...(chef?.cuisineType ? [chef.cuisineType] : []),
          ...(presetTags?.map(t => t.name) || []),
        ]),
      ],
      menu: mappedMenuItems,
      availableForOrdering: acceptingOrders,
    };
  }, [
    id,
    slug,
    name,
    description,
    orderCount,
    chefs,
    bannerImageUrl,
    averageRating,
    liveDeliveryPriceCents,
    preOrderNoticeHour,
    effectiveMinimumOrderAmountCents,
    presetTags,
    mappedMenuItems,
    storeHours,
    acceptingOrders,
  ]);

  const mappedCartItems = useMemo(
    () =>
      checkoutData.map(item => {
        const menu = menuById[item.menuId];
        // Derive quantity/price from the same source of truth the checkout
        // minimum-order check uses (getPresetItemQuantity/getPresetItemTotalPrice),
        // so the cart display can never show a bigger total than what's actually
        // validated at checkout - price is the per-portion average so that
        // price * quantity (as CartSidebar computes it) still equals the real total.
        const totalCents = getPresetItemTotalPrice(item);
        const quantity = getPresetItemQuantity(item);
        // Which size/tier(s) were actually selected, e.g. "12 Pieces x1" -
        // so the cart can show the real breakdown instead of just one number.
        const optionBreakdown = (item.menuItems || [])
          .map(mi => ({
            name: mi.name,
            quantity: parseInt((item.menuItemsMapping || {})[mi.id], 10) || 0,
            price: (mi.priceCents || 0) / 100,
          }))
          .filter(option => option.quantity > 0);
        return {
          id: item.id,
          menuItemId: item.menuId,
          name: item.name,
          quantity,
          price: quantity > 0 ? totalCents / 100 / quantity : 0,
          image:
            menu?.mainImageUrl ||
            menu?.imageUrl ||
            menu?.menuImageUrl ||
            '/panda_avatar.jpg',
          restaurantId: id,
          specialInstructions: item.specialInstruction || '',
          recipientName: extractRecipientName(item.servingDetails),
          optionBreakdown,
          options: [],
        };
      }),
    [checkoutData, menuById, id],
  );

  const subTotal = getPresetSubTotalPrice(checkoutData);
  const totalPrice =
    (subTotal + liveDeliveryPriceCents) * (1 + (preset?.taxRate || 0));

  const openItemModal = mappedItem => {
    if (!acceptingOrders) return;
    const cartId = `Additional_${mappedItem.id}`;
    const existing = checkoutData.find(i => i.id === cartId);
    setActiveItem({
      item: mappedItem,
      initialQuantities: existing?.menuItemsMapping,
      initialInstructions: existing?.specialInstruction,
      initialRecipientName: extractRecipientName(existing?.servingDetails),
    });
    setIsItemModalOpen(true);
  };

  const closeItemModal = () => setIsItemModalOpen(false);

  const onEditItem = cartItem => {
    const menuItem = mappedMenuItems.find(m => m.id === cartItem.menuItemId);
    if (menuItem) openItemModal(menuItem);
  };

  // quantities: { [menuItemOptionId]: portionCount } as selected in the modal
  const onModalAddToCart = (item, quantities, instructions, recipientName) => {
    if (!acceptingOrders) return;
    const originalMenu = menuById[item.id];
    if (!originalMenu) return;
    const totalPortions = Object.values(quantities).reduce(
      (sum, q) => sum + (parseInt(q, 10) || 0),
      0,
    );
    if (totalPortions <= 0) return;
    const cartId = `Additional_${originalMenu.id}`;
    const newPresetItem = {
      ...originalMenu,
      menuId: originalMenu.id,
      chefId: originalMenu.chefId,
      id: cartId,
      presetMenuCategoryName: 'Additional Items',
      quantity: totalPortions,
      menuItemsMapping: { ...quantities },
      specialInstruction: instructions,
      servingDetails: buildServingDetailsFromRecipientName(
        recipientName,
        quantities,
      ),
    };
    const newCheckoutData = buildCheckoutDataFromPresetItem(newPresetItem);
    const alreadyInCart = checkoutData.some(i => i.id === cartId);
    const newCart = alreadyInCart
      ? checkoutData.map(i => (i.id === cartId ? newCheckoutData : i))
      : [...checkoutData, newCheckoutData];
    const newPresetItems = alreadyInCart
      ? (presetItems || []).map(i => (i.id === cartId ? newPresetItem : i))
      : [...(presetItems || []), newPresetItem];
    batch(() => {
      dispatch(setCheckoutData(newCart));
      dispatch(updateUserCart(id, newCart));
      dispatch(setPreset({ ...preset, presetItems: newPresetItems }));
    });
  };

  const onUpdateQuantity = (cartItemId, quantity) => {
    if (quantity <= 0) {
      onRemoveItem(cartItemId);
      return;
    }
    const newCart = checkoutData.map(item => {
      if (item.id !== cartItemId) return item;
      if (item.menuItems && item.menuItems.length > 0) {
        const mapping = item.menuItemsMapping || {};
        const nonZeroKeys = Object.keys(mapping).filter(
          k => (parseInt(mapping[k], 10) || 0) > 0,
        );
        // Only a single selected size/tier can be safely represented by a
        // plain +/- control - if more than one tier has portions, leave the
        // item untouched here; edit it from the item modal instead so we
        // never silently collapse a multi-tier selection into one tier.
        if (nonZeroKeys.length > 1) return item;
        const selectedKey = nonZeroKeys[0] || Object.keys(mapping)[0];
        const newMapping = { ...mapping, [selectedKey]: quantity };
        return { ...item, quantity, menuItemsMapping: newMapping };
      }
      // Items without real serving-size tiers still carry a single
      // placeholder entry in menuItemsMapping (keyed by the menu's own id -
      // see mapMenu's fallback option), because the backend prices from
      // that mapping instead of the top-level quantity. Leaving it frozen
      // at whatever it was when the item was first added silently
      // undercharges every +/- click after that (e.g. 18 in the cart, but
      // the mapping - and the actual bill - stuck at 1).
      if (item.menuItemsMapping && Object.keys(item.menuItemsMapping).length === 1) {
        const [onlyKey] = Object.keys(item.menuItemsMapping);
        return {
          ...item,
          quantity,
          menuItemsMapping: { ...item.menuItemsMapping, [onlyKey]: quantity },
        };
      }
      return { ...item, quantity };
    });
    batch(() => {
      dispatch(setCheckoutData(newCart));
      dispatch(updateUserCart(id, newCart));
    });
  };

  const onRemoveItem = cartItemId => {
    const filteredCheckoutData = checkoutData.filter(
      item => item.id !== cartItemId,
    );
    const filteredPresetItems = (presetItems || []).filter(
      item => item.id !== cartItemId,
    );
    batch(() => {
      dispatch(setCheckoutData(filteredCheckoutData));
      dispatch(updateUserCart(id, filteredCheckoutData));
      dispatch(setPreset({ ...preset, presetItems: filteredPresetItems }));
    });
  };

  const onCheckout = async () => {
    if (!acceptingOrders) {
      store.addNotification({
        message: `${name} is not currently accepting orders.`,
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: { duration: 4000 },
      });
      return;
    }
    if (subTotal === 0) {
      store.addNotification({
        message: 'Your cart is empty.',
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: { duration: 4000 },
      });
      return;
    }
    if (
      receivingMethod === 'delivery' &&
      !checkMinumumHourNoticeValidation(preOrderNoticeHour)
    ) {
      store.addNotification({
        message: `This preset requires ${preOrderNoticeHour} hours notice, please select a delivery date and time that meets the minimum lead time`,
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: { duration: 4000 },
      });
      return;
    }
    if (
      receivingMethod === 'delivery' &&
      totalPrice < effectiveMinimumOrderAmountCents
    ) {
      store.addNotification({
        message: `Minimum $${effectiveMinimumOrderAmountCents / 100} order not met for ${name}. Please add additional items or increase quantity and continue.`,
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: { duration: 4000 },
      });
      return;
    }
    try {
      const userdata = await validate();
      if (userdata) {
        const chef = chefs?.[0];
        const restaurantAddress = [
          chef?.address1,
          chef?.address2,
          chef?.city,
          chef?.state,
          chef?.zip,
          chef?.country,
        ]
          .filter(Boolean)
          .join(', ');
        history.push({
          pathname: '/checkout',
          state: {
            preset,
            deliveryPriceCents: liveDeliveryPriceCents,
            checkoutData,
            restaurantAddress,
          },
        });
      } else {
        store.addNotification({
          message:
            'You are logged out because of inactivity. Please login again!',
          type: 'danger',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animated', 'bounceIn'],
          animationOut: ['animated', 'fadeOut'],
          dismiss: { duration: 4000 },
        });
        dispatch(signoutRequest());
      }
    } catch (e) {
      store.addNotification({
        message:
          'You are logged out because of inactivity. Please login again!',
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: { duration: 4000 },
      });
      dispatch(signoutRequest());
    }
  };

  const onApplyPromo = () => false;

  const onResetCart = () => {
    batch(() => {
      dispatch(resetCheckoutData());
      dispatch(removeUserCart(id));
    });
  };

  const onClickFavorite = () => {
    if (!userSignedIn) {
      dispatch(showSignInModal());
    } else {
      dispatch(toggleRestaurantFavorite(id, favorite));
    }
  };

  const onToggleFavorite = () => {
    onClickFavorite();
    setIsFavorite(!isFavorite);
  };

  const createGroupOrderForPreset = async presetId => {
    setIsCreatingGroupOrder(true);
    try {
      const groupOrder = await createGroupOrder(presetId);
      const groupOrderUrl = getGroupOrderURL(groupOrder.uuid);
      const params = localStorage.getItem('params');
      const urlParam = params
        ? `?${params
            .replace(/":"|":/g, '=')
            .replace(/","|,"/g, '&')
            .replace(/{"|"}|{|}/g, '')}`
        : '';
      window.location.replace(groupOrderUrl + urlParam);
    } catch (e) {
      setIsCreatingGroupOrder(false);
      store.addNotification({
        message: 'Could not create a corporate order link. Please try again.',
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: { duration: 4000 },
      });
    }
  };

  const onCreateGroupOrder = () => {
    if (!acceptingOrders) {
      store.addNotification({
        message: `${name} is not currently accepting orders.`,
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: { duration: 4000 },
      });
      return;
    }
    if (subTotal === 0) {
      store.addNotification({
        message: 'Your cart is empty.',
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: { duration: 4000 },
      });
      return;
    }
    createGroupOrderForPreset(id);
  };

  const onSaveForLater = () => {
    if (subTotal === 0) {
      store.addNotification({
        message: 'Your cart is empty.',
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: { duration: 4000 },
      });
      return;
    }
    setSaveNameInput(`${name} Cart`);
    setShowSaveModal(true);
  };

  // The backend saves this preset asynchronously (a background job), so a
  // 200 response only means the request was accepted, not that the preset
  // actually exists yet. Poll the saved-presets list briefly so we only
  // tell the user it worked once we can actually see it there.
  const waitForSavedPreset = async savedName => {
    for (let attempt = 0; attempt < 5; attempt += 1) {
      // eslint-disable-next-line no-await-in-loop
      await new Promise(resolve => setTimeout(resolve, 1500));
      try {
        // eslint-disable-next-line no-await-in-loop
        const data = await fetchFavoritesAndSavedOrders();
        const allSaved = [
          ...(data?.savedForLaterRestaurants || []),
          ...(data?.savedForLaterPresets || []),
        ];
        if (allSaved.some(p => p.name === savedName)) {
          return true;
        }
      } catch (e) {
        // ignore and retry
      }
    }
    return false;
  };

  const confirmSaveForLater = async () => {
    const newName = saveNameInput.trim();
    if (!newName) return;
    setShowSaveModal(false);
    setIsSavingForLater(true);
    try {
      await saveForLater(newName, preset, checkoutData);
      const confirmed = await waitForSavedPreset(newName);
      if (confirmed) {
        store.addNotification({
          title: 'Cart Saved!',
          message: 'You can find it on your home page under Saved Presets.',
          type: 'success',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animated', 'bounceIn'],
          animationOut: ['animated', 'fadeOut'],
          dismiss: { duration: 4000 },
        });
      } else {
        store.addNotification({
          title: 'Still saving...',
          message:
            "We're still saving your cart. Check your Saved Presets in a moment - if it's not there, try again.",
          type: 'warning',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animated', 'bounceIn'],
          animationOut: ['animated', 'fadeOut'],
          dismiss: { duration: 6000 },
        });
      }
    } catch (e) {
      store.addNotification({
        message: 'Could not save your cart. Please try again.',
        type: 'danger',
        insert: 'top',
        container: 'top-right',
        animationIn: ['animated', 'bounceIn'],
        animationOut: ['animated', 'fadeOut'],
        dismiss: { duration: 4000 },
      });
    } finally {
      setIsSavingForLater(false);
    }
  };

  if (loadingPresetDetails || isEqual(preset, {})) {
    return (
      <main className="pb-16 bg-gray-50/50">
        <DeliveryInfoFormInline
          isAbleToChangeReceivingMethod
          onSubmit={() => {}}
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-6">
          <div className="h-56 sm:h-72 rounded-2xl bg-gray-100 animate-pulse relative flex items-center justify-center overflow-hidden">
            <img
              src="/panda_logo.jpg"
              alt=""
              className="w-14 h-14 rounded-full opacity-20 object-cover"
            />
          </div>
          <div className="grid grid-cols-3 divide-x divide-gray-100 border-b border-gray-100 bg-white rounded-b-none rounded-t-none -mt-1">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="p-4 sm:p-5 text-center space-y-2">
                <div className="h-2.5 w-16 bg-gray-100 rounded animate-pulse mx-auto" />
                <div className="h-4 w-12 bg-gray-100 rounded animate-pulse mx-auto" />
              </div>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start mt-6">
            <div className="lg:col-span-8 space-y-4">
              <div className="h-6 w-32 bg-gray-100 rounded-lg animate-pulse" />
              {Array.from({ length: 5 }).map((_, i) => (
                <div
                  key={i}
                  className="flex items-center gap-4 bg-white rounded-2xl border border-gray-200/80 p-3.5"
                >
                  <div className="w-20 h-20 rounded-xl bg-gray-100 animate-pulse flex-shrink-0" />
                  <div className="flex-1 space-y-2.5">
                    <div className="h-4 w-2/5 bg-gray-100 rounded animate-pulse" />
                    <div className="h-3 w-4/5 bg-gray-100 rounded animate-pulse" />
                    <div className="h-3 w-1/5 bg-gray-100 rounded animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
            <div className="hidden lg:block lg:col-span-4 sticky top-28">
              <div className="bg-white rounded-2xl border border-gray-200/80 p-5 space-y-4">
                <div className="h-5 w-28 bg-gray-100 rounded animate-pulse" />
                <div className="h-24 bg-gray-100 rounded-xl animate-pulse" />
                <div className="h-3 w-full bg-gray-100 rounded animate-pulse" />
                <div className="h-3 w-full bg-gray-100 rounded animate-pulse" />
                <div className="h-10 w-full bg-gray-100 rounded-xl animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="pb-16 bg-gray-50/50">
      {(isCreatingGroupOrder || isSavingForLater) && <LoadingAnimation />}
      <DeliveryInfoFormInline
        isAbleToChangeReceivingMethod
        onSubmit={() => setDeliveryBarVersion(v => v + 1)}
      />
      <RestaurantView
        restaurant={mappedRestaurant}
        onBack={() => history.push('/browse-restaurants')}
        onOpenItemModal={openItemModal}
        cartItems={mappedCartItems}
        onUpdateQuantity={onUpdateQuantity}
        onRemoveItem={onRemoveItem}
        onEditItem={onEditItem}
        onCheckout={onCheckout}
        onSaveForLater={onSaveForLater}
        onCreateGroupOrder={onCreateGroupOrder}
        isSavingForLater={isSavingForLater}
        isCreatingGroupOrder={isCreatingGroupOrder}
        appliedPromo=""
        onApplyPromo={onApplyPromo}
        discountAmount={0}
        taxRate={preset?.taxRate || 0}
      />
      {activeItem && (
        <ItemModal
          item={activeItem.item}
          restaurant={mappedRestaurant}
          isOpen={isItemModalOpen}
          onClose={closeItemModal}
          onAddToCart={onModalAddToCart}
          initialQuantities={activeItem.initialQuantities}
          initialInstructions={activeItem.initialInstructions}
          initialRecipientName={activeItem.initialRecipientName}
        />
      )}
      {showSaveModal && (
        <div className="fixed inset-0 z-[70] bg-[#202124]/60 backdrop-blur-xs flex items-center justify-center px-4">
          <div className="w-full max-w-sm bg-white rounded-2xl shadow-2xl p-5">
            <h3 className="font-heading font-bold text-lg text-[#202124] mb-1">
              Name this saved cart
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              You can find it later from your home page under Saved Presets.
            </p>
            <form
              onSubmit={e => {
                e.preventDefault();
                confirmSaveForLater();
              }}
            >
              <input
                autoFocus
                type="text"
                value={saveNameInput}
                onChange={e => setSaveNameInput(e.target.value)}
                placeholder="ex: Dec 25th, Christmas order"
                className="w-full px-3 py-2 text-sm bg-white rounded-xl border border-gray-200 text-[#202124] focus:outline-none focus:ring-1 focus:ring-[#E53935] mb-4"
              />
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setShowSaveModal(false)}
                  className="flex-1 py-2.5 rounded-xl bg-gray-100 hover:bg-gray-200 text-gray-700 font-heading font-bold text-sm transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!saveNameInput.trim()}
                  className="flex-1 py-2.5 rounded-xl bg-[#E53935] hover:bg-[#d32f2f] disabled:opacity-50 disabled:cursor-not-allowed text-white font-heading font-bold text-sm transition-colors"
                >
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </main>
  );
};

export default PandaRestaurantDetails;
